import { DASHBOARD_TYPE } from 'domain/entity/reportTemplate';

export const DASHBOARDS_LIMIT = 1000;

export enum DashboardAccessVisibility {
    PUBLIC = 'PUBLIC',
    PRIVATE = 'PRIVATE',
    GLOBAL = 'GLOBAL',
}

export enum DashboardPermittedAction {
    DELETE = 'DELETE',
    EDIT = 'EDIT',
    LINK = 'LINK',
    SHARE = 'SHARE',
    PIN = 'PIN',
    DUPLICATE = 'DUPLICATE',
}

export enum UserGroupLevel {
    EDIT = 'EDIT',
    VIEW = 'VIEW',
    NONE = 'NONE',
}

export class DashboardUserGroup {
    constructor(readonly id: string, readonly name: string, readonly level: UserGroupLevel) {
        if (id === undefined || name === undefined || level === undefined) {
            throw new ReferenceError('Undefined prop');
        }
    }
}

export type DashboardPermittedActions = {
    delete: boolean;
    edit: boolean;
    link: boolean;
    share: boolean;
    pin: boolean;
    duplicate: boolean;
};

export type DashboardAccess = {
    visibility: DashboardAccessVisibility;
    groups: DashboardUserGroup[];
};

export type DashboardOwner = {
    me: boolean;
    name?: string;
    email?: string;
};

export const getOwnerDisplayName = (owner: DashboardOwner): string | undefined => owner.name || owner.email || undefined;

export type DashboardFrameHeader = {
    logo: boolean;
    templateName: boolean;
    period: boolean;
    location: boolean;
};

export type DashboardFrameFooter = {
    generationDate: boolean;
    pageNumber: boolean;
};

export type DashboardFrame = {
    header: DashboardFrameHeader;
    footer: DashboardFrameFooter;
};

export const DASHBOARD_FRAME_HEADER_KEYS = [
    'logo',
    'templateName',
    'period',
    'location',
] as const satisfies readonly (keyof DashboardFrameHeader)[]

export const DASHBOARD_FRAME_FOOTER_KEYS = [
    'generationDate',
    'pageNumber',
] as const satisfies readonly (keyof DashboardFrameFooter)[]

export type FrameKey =
    | (typeof DASHBOARD_FRAME_HEADER_KEYS)[number]
    | (typeof DASHBOARD_FRAME_FOOTER_KEYS)[number]

export type FrameData = Partial<Record<FrameKey, string>>;

class Dashboard {
    constructor(
        readonly id: string,
        readonly name: string,
        readonly pinned: boolean,
        readonly access: DashboardAccess,
        readonly owner: DashboardOwner,
        readonly permittedActions: DashboardPermittedActions,
        readonly starred: boolean,
        readonly type: DASHBOARD_TYPE,
        readonly description?: string,
        readonly frame?: DashboardFrame
    ) {}

    public copyWithName(name: string): Dashboard {
        return new Dashboard(this.id, name, false, this.access, this.owner, this.permittedActions, this.starred, this.type, this.description, this.frame);
    }

    public copyWith({
        name,
        access,
        owner,
        permittedActions,
    }: {
        name?: string;
        access?: DashboardAccess;
        owner?: DashboardOwner;
        permittedActions?: DashboardPermittedActions;
    }): Dashboard {
        return new Dashboard(
            this.id,
            name ?? this.name,
            false,
            access ?? this.access,
            owner ?? this.owner,
            permittedActions ?? this.permittedActions,
            this.starred,
            this.type,
            this.description,
            this.frame
        );
    }
}

export default Dashboard;
